import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { IconX } from '@tabler/icons-react';
import { Button } from 'design-system/button/button';
import { cn } from 'lib/cn';
import { type ComponentProps } from 'react';

const Sheet = ({ ...props }: SheetPrimitive.Root.Props) => (
  <SheetPrimitive.Root data-slot="sheet" {...props} />
);
Sheet.displayName = 'Sheet';

const SheetTrigger = ({ ...props }: SheetPrimitive.Trigger.Props) => (
  <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
);
SheetTrigger.displayName = 'SheetTrigger';

const SheetClose = ({ ...props }: SheetPrimitive.Close.Props) => (
  <SheetPrimitive.Close data-slot="sheet-close" {...props} />
);
SheetClose.displayName = 'SheetClose';

const SheetPortal = ({ ...props }: SheetPrimitive.Portal.Props) => (
  <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
);
SheetPortal.displayName = 'SheetPortal';

const SheetOverlay = ({
  className,
  ...props
}: SheetPrimitive.Backdrop.Props) => (
  <SheetPrimitive.Backdrop
    data-slot="sheet-overlay"
    className={cn(
      'bg-overlay fixed inset-0 z-50 supports-backdrop-filter:backdrop-blur-xs motion-safe:transition-opacity motion-safe:duration-150 motion-safe:data-ending-style:opacity-0 motion-safe:data-starting-style:opacity-0',
      className,
    )}
    {...props}
  />
);
SheetOverlay.displayName = 'SheetOverlay';

const SheetContent = ({
  className,
  overlayClassName,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  showCloseButton?: boolean;
  overlayClassName?: string;
}) => (
  <SheetPortal>
    <SheetOverlay className={overlayClassName} />
    <SheetPrimitive.Popup
      data-slot="sheet-content"
      data-side={side}
      className={cn(
        'bg-surface-elevated-secondary text-foreground border-line shadow-overlay fixed z-50 flex flex-col gap-4 bg-clip-padding p-4 text-sm data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:rounded-t-2xl data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out motion-safe:data-ending-style:opacity-0 motion-safe:data-starting-style:opacity-0 motion-safe:data-[side=bottom]:data-ending-style:translate-y-[2.5rem] motion-safe:data-[side=bottom]:data-starting-style:translate-y-[2.5rem] motion-safe:data-[side=left]:data-ending-style:translate-x-[-2.5rem] motion-safe:data-[side=left]:data-starting-style:translate-x-[-2.5rem] motion-safe:data-[side=right]:data-ending-style:translate-x-[2.5rem] motion-safe:data-[side=right]:data-starting-style:translate-x-[2.5rem] motion-safe:data-[side=top]:data-ending-style:translate-y-[-2.5rem] motion-safe:data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton ? (
        <SheetPrimitive.Close
          data-slot="sheet-close"
          render={
            <Button
              variant="ghost"
              className="absolute top-3 right-3"
              size="icon"
            />
          }
        >
          <IconX />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      ) : null}
    </SheetPrimitive.Popup>
  </SheetPortal>
);
SheetContent.displayName = 'SheetContent';

const SheetHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="sheet-header"
    className={cn('flex flex-col gap-0.5', className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="sheet-footer"
    className={cn('mt-auto flex flex-col gap-2', className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = ({ className, ...props }: SheetPrimitive.Title.Props) => (
  <SheetPrimitive.Title
    data-slot="sheet-title"
    className={cn('text-foreground text-base font-medium', className)}
    {...props}
  />
);
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = ({
  className,
  ...props
}: SheetPrimitive.Description.Props) => (
  <SheetPrimitive.Description
    data-slot="sheet-description"
    className={cn('text-foreground-secondary text-sm', className)}
    {...props}
  />
);
SheetDescription.displayName = 'SheetDescription';

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
